import { Edge, useReactFlow } from '@xyflow/react';
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import {
  ActionParameter,
  PrimitiveActionParameter,
  PrimitiveParameterValue,
  TekstNode,
} from 'src/features/automations/[flowId]/components/utils/NodeDataTypes';
import { useFlowActions } from 'src/features/flows/queries/config/useFlowActions';
import { useFlowApps } from 'src/features/flows/queries/config/useFlowApps';
import { FlowConstructionStatus } from 'src/features/flows/contexts/FlowContext';
import { useIntegrations } from 'src/features/integrations/queries/useIntegrations';
import { deepEqual } from 'src/features/flows/lib/general';
import { FlowIssue } from 'src/features/flows/lib/validators';
import { Flow } from 'src/features/flows/types/flow.types';
import { IntegrationId } from 'src/shared/types/integration.types';
import { useFlowStore } from 'src/features/flows/contexts/FlowStoreProvider';

// Recursive function to clean overrides from action parameters
function recursivelyCleanOverrides(actionParam: ActionParameter, newIntegrations: IntegrationId[]): ActionParameter {
  if (actionParam.type === 'custom[]') {
    return {
      ...actionParam,
      values: actionParam.values.map((value) => recursivelyCleanOverrides(value, newIntegrations)),
    };
  }
  return {
    ...actionParam,
    overrides: newIntegrations.reduce(
      (acc, integrationId) => {
        if (actionParam.overrides?.[integrationId]) {
          acc[integrationId] = actionParam.overrides[integrationId];
        }
        return acc;
      },
      {} as { [integrationId: IntegrationId]: PrimitiveParameterValue[] }
    ),
  };
}

// this hook is used to validate the flow and return issues
// it also handles cleaning up overrides
function useFlowValidator(currentFlow: Flow | undefined, constructionStatus: FlowConstructionStatus) {
  const flowStore = useFlowStore();
  const workerRef = useRef<Worker | null>(null);
  const { data: tekstApps } = useFlowApps();
  const { setNodes } = useReactFlow<TekstNode>();
  const { integrationMap, isLoading } = useIntegrations();
  const { data: fullFlowActions } = useFlowActions(currentFlow, 'full');
  const [flowIssues, setFlowIssues] = useState<FlowIssue[]>([]);

  // Get the current nodes and edges from FlowStore (not ReactFlow)
  // This ensures we always validate the latest data, even if ReactFlow hasn't re-rendered
  const [nodes, setNodesState] = useState<TekstNode[]>(() => flowStore.toReactFlowNodes());
  const [edges, setEdgesState] = useState<Edge[]>(() => flowStore.toReactFlowEdges());

  // Subscribe to FlowStore changes (once on mount)
  useEffect(() => {
    const unsubscribe = flowStore.subscribe(() => {
      const newNodes = flowStore.toReactFlowNodes();
      const newEdges = flowStore.toReactFlowEdges();

      setNodesState((prevNodes) => {
        if (!deepEqual(prevNodes, newNodes)) {
          return newNodes;
        }
        return prevNodes;
      });

      setEdgesState((prevEdges) => {
        if (!deepEqual(prevEdges, newEdges)) {
          return newEdges;
        }
        return prevEdges;
      });
    });

    return () => {
      unsubscribe();
    };
  }, [flowStore]);

  const onValidationResult = useEffectEvent((issues: FlowIssue[]) => {
    if (!deepEqual(issues, flowIssues)) {
      setFlowIssues(issues);
    }
  });

  // Initialize the worker once on mount
  useEffect(() => {
    // Create the worker only on the client side
    if (typeof window !== 'undefined') {
      workerRef.current = new Worker(new URL('../../../../shared/workers/flowValidation.worker.ts', import.meta.url));

      // Set up the message handler
      workerRef.current.onmessage = (event: MessageEvent) => {
        if (event.data.type === 'VALIDATION_RESULT') {
          onValidationResult(event.data.issues);
        }
      };

      // Clean up the worker when the component unmounts
      return () => {
        workerRef.current?.terminate();
        workerRef.current = null;
      };
    }
  }, []);

  // Run validation when dependencies change
  useEffect(() => {
    if (constructionStatus !== 'DONE') {
      return;
    }

    // Guard against incomplete node data
    // If we only have trigger + tekst nodes, we're still loading other nodes
    const nodeTypes = new Set(nodes.map((node) => node.data.type));
    const lacksRequiredNodes = !nodeTypes.has('action') && !nodeTypes.has('app') && !nodeTypes.has('utility');

    if (lacksRequiredNodes) {
      return;
    }

    if (workerRef.current && fullFlowActions && currentFlow && !isLoading && tekstApps && nodes && edges) {
      workerRef.current.postMessage({
        type: 'VALIDATE_FLOW',
        nodes,
        edges,
        possibleActions: fullFlowActions,
        flow: currentFlow,
        integrationMap,
        tekstApps,
      });
    }
  }, [nodes, edges, constructionStatus, fullFlowActions, currentFlow, integrationMap, isLoading, tekstApps]);

  // Function to clean overrides from nodes in the flow when integrations change
  const cleanOverrides = useCallback(
    (newIntegrations: IntegrationId[]) => {
      // Update nodes in the reactflow instance
      setNodes((nodes) =>
        nodes.map((node) => {
          // Clean parameters for action nodes
          let cleanedParameters: ActionParameter[] = [];
          switch (node.data.type) {
            case 'utility':
            case 'action':
              cleanedParameters = node.data.actionParameters.map((actionParam) =>
                recursivelyCleanOverrides(actionParam, newIntegrations)
              );
              node.data.actionParameters = cleanedParameters as PrimitiveActionParameter[];
              break;

            case 'app':
              cleanedParameters = node.data.variables.map((actionParam) =>
                recursivelyCleanOverrides(actionParam, newIntegrations)
              );
              node.data.variables = cleanedParameters as PrimitiveActionParameter[];
              break;
          }
          return node;
        })
      );
    },
    [setNodes]
  );

  // ✅ Memoize return object - prevents FlowContext from changing when validator re-runs
  // Only creates new object when flowIssues or cleanOverrides actually change
  return useMemo(() => ({ flowIssues, cleanOverrides }), [flowIssues, cleanOverrides]);
}

export default useFlowValidator;
